export interface ITransactionCertificateReqDto {
  tcId?: string;
  tcNo: string;
  tcVersionNo: string;
  tcStandard: string[];
  tcStatus: string;
  tcAmendmentReason: string;
  tcInvalidationReason: string;
  tcPlaceOfIssue: string;
  tcIssueDate: string;
  tcLastUpdatedDate: string;
  tcCbLicensingCode: string;
  tcApplicationReceivedDate: string;
  tcApplicationCompleteDate: string;
  tcDelayReason: string;
  tcCreatedBy: string;
  tcUsdaNopCompliantFlag: string;
  isVersionUpdated: string;
  tcIsReturn: string;
  returnTcId: string;
  returnTcNo: string;
  returnTcVersionNo: string;
  returnTcSourceVersion: string;
  tcCreationDate: string;
  tcCbLicensingCodeDescription: string;
  // Add description fields
  tcStatusDescription?: string;
  tcAmendmentReasonDescription?: string;
  tcInvalidationReasonDescription?: string;
  tcDelayReasonDescription?: string;
  tcStandardDescription?: string[];
}

export const transactionCertificateReqDto: ITransactionCertificateReqDto = {
  tcNo: "",
  tcAmendmentReason: "",
  tcVersionNo: "",
  tcStandard: [],
  tcStatus: "",
  tcInvalidationReason: "",
  tcPlaceOfIssue: "",
  tcIssueDate: "",
  tcLastUpdatedDate: "",
  tcCbLicensingCode: "",
  tcApplicationReceivedDate: "",
  tcDelayReason: "",
  tcCreatedBy: "",
  tcApplicationCompleteDate: "",
  tcUsdaNopCompliantFlag: "",
  isVersionUpdated: "",
  tcIsReturn: "",
  returnTcId: "",
  returnTcNo: "",
  returnTcVersionNo: "",
  returnTcSourceVersion: "",
  tcCreationDate: "",
  tcCbLicensingCodeDescription: ""
}

// Helper function to convert API tcIsReturn value (Y/N) to form value (Yes/No)
const convertIsReturnValue = (value: string | undefined): string => {
  if (!value) return '';
  const normalized = value.toUpperCase().trim();
  if (normalized === 'Y') return 'Yes';
  if (normalized === 'N') return 'No';
  return value; // Return original if it's already in the correct format
};

export const mapToTransactionCertificateReqDto = (data?: any, TE_ID?: any, isVersionUpdated?: boolean, cbLicenseCode?: any): ITransactionCertificateReqDto => {
  return {
    ...(TE_ID ? { tcId: TE_ID } : {}),
    isVersionUpdated: isVersionUpdated ? "yes" : "no",
    tcNo: data.tcNo || '',
    tcVersionNo: data.tcVersionNo || '',
    tcStandard: data.tcStandard || [],
    tcStatus: data.tcStatus || '',
    tcStatusDescription: data.tcStatus === 'V' ? 'Valid' :
      data.tcStatus === 'I' ? 'Invalid' :
        data.tcStatus === 'A' ? 'Amended' :
          data.tcStatus === 'R' ? 'Return' :
            data.tcStatus === 'RA' ? 'Return Amended' :
              data.tcStatus === 'RI' ? 'Return Invalidated' : '',
    tcAmendmentReason: data.tcAmendmentReason || '',
    tcInvalidationReason: data.tcInvalidationReason || '',
    tcPlaceOfIssue: data.tcPlaceOfIssue || '',
    tcIssueDate: data.tcIssueDate || '',
    tcLastUpdatedDate: data.tcLastUpdatedDate || '',
    tcCbLicensingCode: cbLicenseCode || '',
    tcApplicationReceivedDate: data.tcApplicationReceivedDate || '',
    tcApplicationCompleteDate: data.tcApplicationCompleteDate || '',
    tcDelayReason: data.tcDelayReason || '',
    tcCreatedBy: data.tcCreatedBy || '',
    tcUsdaNopCompliantFlag: data.tcUsdaNopCompliantFlag || '',
    tcIsReturn: convertIsReturnValue(data.tcIsReturn),
    returnTcId: data.returnTcId || '',
    returnTcNo: data.returnTcNo || '',
    returnTcVersionNo: data.returnTcVersionNo || '',
    returnTcSourceVersion: data.returnTcSourceVersion || '',
    tcCreationDate: data.tcCreationDate || '',
    tcCbLicensingCodeDescription: data.tcCbLicensingCodeDescription || '',
    tcAmendmentReasonDescription: data.tcAmendmentReason === 'R' ? 'Reduction in the Quantity of Claimed Material' :
      data.tcAmendmentReason === 'T' ? 'Typographical Errors within Fourteen Calendar Days of Issuance' :
        data.tcAmendmentReason === 'C' ? 'Certification Body Errors' :
          data.tcAmendmentReason === 'I' ? 'Incorrect Information' :
            data.tcAmendmentReason === 'D' ? 'Duplicate Transaction Certificate' :
              data.tcAmendmentReason === 'E' ? 'Exemption Issued by Textile Exchange' : '',
    tcInvalidationReasonDescription: data.tcInvalidationReason === 'F' ? 'Falsified Or Fraudulent Documents' :
      data.tcInvalidationReason === 'E' ? 'Claimed Materials are/will be Included in an e-transaction' :
        data.tcInvalidationReason === 'I' ? 'Applicable Input Transaction Has Been Invalidated' :
          data.tcInvalidationReason === 'M' ? 'Claimed Materials Are Shown To Be Ineligible' :
            data.tcInvalidationReason === 'S' ? 'Requests From The Seller' :
              data.tcInvalidationReason === 'O' ? 'Other Reason' :
                data.tcInvalidationReason === 'C' ? 'Certification Body Errors' : '',
    tcDelayReasonDescription: data.tcDelayReason === 'A' ? 'Acceptable Animal Fiber Exception' :
      data.tcDelayReason === 'D' ? 'Acceptable Transport Documentation Authentication Exception' :
        data.tcDelayReason === 'E' ? 'Exemption Issued by Textile Exchange' :
          data.tcDelayReason === 'F' ? 'Acceptable Sellers with Final Products as Inputs Exception' :
            data.tcDelayReason === 'I' ? 'Input Transaction Certificate Issued Late' :
              data.tcDelayReason === 'na' ? 'Not Applicable' : '',

    // For multi-select fields
    tcStandardDescription: Array.isArray(data.tcStandard)
      ? data.tcStandard.map((code: string) => {
        const option = [
          { label: "Organic Content Standard (OCS)", value: "OCS" },
          { label: "Responsible Down Standard (RDS)", value: "RDS" },
          { label: "Responsible Alpaca Standard (RAS)", value: "RAS" },
          { label: "Global Recycled Standard (GRS)", value: "GRS" },
          { label: "Responsible Wool Standard (RWS)", value: "RWS" },
          { label: "Content Claim Standard (CCS)", value: "CCS" },
          { label: "Recycled Claim Standard (RCS)", value: "RCS" },
          { label: "Responsible Mohair Standard (RMS)", value: "RMS" },
          { label: "Sustainable Fiber Alliance (SFA)", value: "SFA" },
        ].find(opt => opt.value === code);
        return option?.label || "";
      })
      : (data.tcStandard ? [data.tcStandard] : []),
  };
};
